import {
  Box, Button, IconButton, Paper, Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import useInstallPrompt from '../hooks/useInstallPrompt'
import useWebPush from './useWebPush'

const DISMISSED_KEY = 'web_push_banner_dismissed'
const DISMISSED_TTL_DAYS = 14

function dismissedRecently() {
  try {
    const timestamp = Number(localStorage.getItem(DISMISSED_KEY))
    return !!timestamp && Date.now() < timestamp + DISMISSED_TTL_DAYS * 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}

// Asks once in a while to turn on push on this device. The browser only asks for the permission
// after a tap, on the iPhone only in the app from the home screen (the install banner explains that).
const PushBanner: FunctionComponent = () => {
  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const { canInstall } = useInstallPrompt()
  const { state, busy, enable } = useWebPush()
  const [dismissed, setDismissed] = useState(dismissedRecently)

  const isSharedLink = new URLSearchParams(search).has('access_token')
    || pathname.startsWith('/embedded')
    || pathname.startsWith('/resultset/')
    || pathname === '/thanks'
    || pathname.startsWith('/link/')

  if (state !== 'off' || dismissed || canInstall || isSharedLink) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, Date.now().toString())
    } catch {
      // the banner just comes back next time
    }
    setDismissed(true)
  }

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        bottom: { xs: 64, sm: 16 }, // above the bottom navigation on phones
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        maxWidth: 440,
        width: 'calc(100% - 32px)',
        bgcolor: 'background.paper'
      }}
    >
      <NotificationsActiveIcon color="primary" sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2">{t('webPush.banner.title')}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('webPush.banner.text')}
        </Typography>
      </Box>
      <Button size="small" variant="contained" onClick={enable} disabled={busy} sx={{ flexShrink: 0 }}>
        {t('webPush.enable')}
      </Button>
      <IconButton size="small" onClick={dismiss} aria-label={t('webPush.banner.later')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  )
}

export default PushBanner
